import { useEffect, useState } from 'react';

export interface D20RollResult {
  /** 1 або 2 значення (2 — при advantage/disadvantage). */
  dice: number[];
  /** Індекс у dice, який пішов у total. */
  pickedIndex: number;
  modifier: number;
  total: number;
  verdict: 'crit' | 'fail' | 'normal';
}

export interface D20RollerProps {
  onClose?: () => void;
  onRollComplete?: (r: D20RollResult) => void;
  label?: string;
  initialModifier?: number;
  initialAdvantage?: 'none' | 'advantage' | 'disadvantage';
  autoRoll?: boolean;
  /** Playback з лога — показує задані значення без рандому. */
  playback?: { dice: number[]; pickedIndex: number; modifier?: number };
}

/**
 * GM-кидок на влучання: 1d20 (або 2d20 + take higher/lower при advantage/disadvantage).
 * nat 20 → crit, nat 1 → fail.
 */
export function D20Roller({
  onClose, onRollComplete, label, initialModifier = 0,
  initialAdvantage = 'none', autoRoll = false, playback,
}: D20RollerProps): JSX.Element {
  const [dice, setDice] = useState<number[] | null>(playback?.dice ?? null);
  const [pickedIndex, setPickedIndex] = useState<number>(playback?.pickedIndex ?? 0);
  const [rolling, setRolling] = useState(false);
  const [modifier, setModifier] = useState(playback?.modifier ?? initialModifier);
  const [adv, setAdv] = useState<'none' | 'advantage' | 'disadvantage'>(
    playback ? (playback.dice.length === 2 ? 'advantage' : 'none') : initialAdvantage,
  );

  const roll = (): void => {
    setRolling(true);
    const twin = adv !== 'none';
    const interval = setInterval(() => {
      const a = 1 + Math.floor(Math.random() * 20);
      setDice(twin ? [a, 1 + Math.floor(Math.random() * 20)] : [a]);
    }, 60);
    setTimeout(() => {
      clearInterval(interval);
      const a = 1 + Math.floor(Math.random() * 20);
      let finalDice: number[];
      let pick: number;
      if (twin) {
        const b = 1 + Math.floor(Math.random() * 20);
        finalDice = [a, b];
        pick = adv === 'advantage' ? (a >= b ? 0 : 1) : (a <= b ? 0 : 1);
      } else {
        finalDice = [a];
        pick = 0;
      }
      setDice(finalDice);
      setPickedIndex(pick);
      setRolling(false);
      if (onRollComplete) {
        const picked = finalDice[pick]!;
        const total = picked + modifier;
        const verdict: 'crit' | 'fail' | 'normal' = picked === 20 ? 'crit' : picked === 1 ? 'fail' : 'normal';
        onRollComplete({ dice: finalDice, pickedIndex: pick, modifier, total, verdict });
      }
    }, 600);
  };

  useEffect(() => {
    if (autoRoll && !playback && dice === null) {
      const t = setTimeout(() => roll(), 100);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRoll, playback]);

  useEffect(() => {
    if (playback) {
      setRolling(true);
      const t = setTimeout(() => setRolling(false), 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [playback]);

  const reset = (): void => {
    setDice(null); setPickedIndex(0); setRolling(false);
    setModifier(0); setAdv('none');
  };

  const picked = dice ? dice[pickedIndex] : null;
  const total = picked !== null && picked !== undefined ? picked + modifier : null;
  const verdict: 'crit' | 'fail' | 'normal' | null = picked === null || picked === undefined
    ? null
    : picked === 20 ? 'crit' : picked === 1 ? 'fail' : 'normal';

  const toneColor = (v: 'crit' | 'fail' | 'normal'): string =>
    v === 'crit' ? 'var(--gold-bright)' : v === 'fail' ? 'var(--blood-bright)' : 'var(--text)';

  return (
    <div style={{ fontFamily: 'Spectral, serif', color: 'var(--text)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
        <h2 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--gold-bright)', margin: 0, fontSize: 20 }}>
          {label ?? 'Майстер: кидок на влучання'}
        </h2>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: 'var(--text-dim)',
            fontSize: 20, cursor: 'pointer', padding: '0 8px',
          }}>×</button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 18 }}>
        {(dice ?? [null, ...(adv !== 'none' ? [null] : [])]).map((v, i) => (
          <D20Die
            key={i}
            value={v as number | null}
            rolling={rolling}
            faded={dice !== null && pickedIndex !== i}
          />
        ))}
      </div>

      {verdict !== null && !rolling && (
        <div style={{
          padding: 16,
          margin: '0 0 16px',
          background: 'var(--bg-card)',
          border: `1px solid ${toneColor(verdict)}`,
          borderRadius: 4,
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <div style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 18,
              color: toneColor(verdict),
              fontWeight: 600,
            }}>
              {verdict === 'crit' ? 'Критичний влучання' : verdict === 'fail' ? 'Природний провал' : 'Кидок'}
            </div>
            <div style={{ marginTop: 8, fontFamily: 'Spline Sans Mono, monospace', fontSize: 11, color: 'var(--text-dim)' }}>
              {dice && dice.length === 2
                ? `2d20 = ${dice.join(' / ')} → взято ${adv === 'advantage' ? 'більше' : 'менше'}: ${picked}`
                : `d20 = ${picked}`}
              {modifier !== 0 && ` ${modifier > 0 ? '+' : ''}${modifier} (mod)`}
            </div>
          </div>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 40,
            color: toneColor(verdict),
            fontWeight: 700,
            minWidth: 60,
            textAlign: 'right',
          }}>{total}</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontFamily: 'Spline Sans Mono, monospace', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          Модифікатор
        </span>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[-2, -1, 0, 1, 2, 3, 4, 5].map((m) => (
            <button key={m} onClick={() => setModifier(m)} style={modBtnStyle(modifier === m)}>
              {m > 0 ? `+${m}` : m}
            </button>
          ))}
        </div>

        <span style={{ fontSize: 11, fontFamily: 'Spline Sans Mono, monospace', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          Перевага
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => { setAdv('disadvantage'); setDice(null); }} style={modBtnStyle(adv === 'disadvantage')}>недолік</button>
          <button onClick={() => { setAdv('none'); setDice(null); }} style={modBtnStyle(adv === 'none')}>—</button>
          <button onClick={() => { setAdv('advantage'); setDice(null); }} style={modBtnStyle(adv === 'advantage')}>перевага</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={roll} disabled={rolling} style={primaryBtn}>
          {rolling ? 'Кидаю…' : adv === 'none' ? 'Roll d20' : 'Roll 2d20'}
        </button>
        <button onClick={reset} style={ghostBtn}>Скинути</button>
      </div>

      <style>{`
        @keyframes d20-tumble {
          0%   { transform: rotate(0deg) scale(1); }
          50%  { transform: rotate(180deg) scale(1.05); }
          100% { transform: rotate(360deg) scale(1); }
        }
        @keyframes d20-settle {
          0%   { transform: scale(1.15) rotate(-8deg); }
          60%  { transform: scale(0.95) rotate(4deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
      `}</style>
    </div>
  );
}

function D20Die({ value, rolling, faded = false }: {
  value: number | null;
  rolling: boolean;
  faded?: boolean;
}): JSX.Element {
  const size = 100;
  const palette = { bg1: '#7d97c9', bg2: '#4a6592', bg3: '#2a3e63', stroke: '#0e1830', face: '#f2efe6', glow: 'rgba(125,151,201,0.55)' };

  const cx = size / 2;
  const cy = size / 2;
  // d20 = isohedron, проектуємо як трикутник з 3-кутників. Спрощено: великий трикутник, маленький усередині.
  const r = size * 0.46;
  const triOuter: Array<[number, number]> = [];
  for (let i = 0; i < 3; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / 3;
    triOuter.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  const triInner = triOuter.map(([x, y]) => [cx + (x - cx) * 0.5, cy + (y - cy) * 0.5]);
  const outerPts = triOuter.map((p) => p.join(',')).join(' ');
  const innerPts = triInner.map((p) => p.join(',')).join(' ');

  return (
    <div style={{
      position: 'relative', width: size, height: size, display: 'inline-block',
      opacity: faded ? 0.35 : 1,
      filter: `drop-shadow(0 0 ${rolling ? 18 : 8}px ${palette.glow})`,
      transition: 'opacity .2s, filter .2s',
    }}>
      <div style={{
        width: size, height: size,
        animation: rolling
          ? 'd20-tumble 0.45s linear infinite'
          : (value !== null ? 'd20-settle 0.6s cubic-bezier(0.18, 1.5, 0.4, 1)' : 'none'),
        transformOrigin: 'center',
      }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
          <defs>
            <radialGradient id="d20-bg" cx="0.4" cy="0.35">
              <stop offset="0%" stopColor={palette.bg1} />
              <stop offset="55%" stopColor={palette.bg2} />
              <stop offset="100%" stopColor={palette.bg3} />
            </radialGradient>
          </defs>
          <polygon points={outerPts} fill="url(#d20-bg)" stroke={palette.stroke} strokeWidth="1.4" />
          <polygon points={innerPts} fill="rgba(255,255,255,0.08)" stroke={palette.stroke} strokeWidth="1" />
          <text
            x={cx}
            y={cy + size * 0.10}
            textAnchor="middle"
            fontFamily="'Cormorant Garamond', 'Cormorant Garamond', serif"
            fontWeight={700}
            fontSize={size * 0.30}
            fill={palette.face}
          >{value ?? '?'}</text>
        </svg>
      </div>
      <div style={{
        position: 'absolute', bottom: -16, left: 0, right: 0,
        textAlign: 'center',
        fontFamily: 'Spline Sans Mono, monospace',
        fontSize: 9, color: palette.bg2,
        textTransform: 'uppercase', letterSpacing: '0.14em',
      }}>d20</div>
    </div>
  );
}

const modBtnStyle = (active: boolean): React.CSSProperties => ({
  background: active ? 'var(--gold-dim)' : 'var(--bg-card)',
  color: active ? 'var(--bg-deep)' : 'var(--text-muted)',
  border: `1px solid ${active ? 'var(--gold)' : 'var(--border)'}`,
  borderRadius: 3,
  padding: '4px 10px',
  fontFamily: 'Spline Sans Mono, monospace',
  fontSize: 11,
  cursor: 'pointer',
  minWidth: 38,
});

const primaryBtn: React.CSSProperties = {
  flex: 1,
  background: 'linear-gradient(180deg, rgba(204,167,102,0.22), rgba(204,167,102,0.10))',
  color: 'var(--gold-bright)',
  border: '1px solid var(--gold-dim)',
  borderRadius: 4,
  padding: '10px 16px',
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: 14,
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '10px 16px',
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: 13,
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  cursor: 'pointer',
};
